import React from 'react';
import { motion } from 'framer-motion';
import { Package, RotateCcw, Clock, Shield } from 'lucide-react';
import Header from '../components/Header';
import { BRAND } from '../data/brand';

const SHIPPING_INFO = [
  {
    icon: Package,
    title: 'Discreet Packaging',
    description: 'Every order ships in a plain, unmarked box. No logos, no branding, no indication of contents. Your privacy comes first.',
  },
  {
    icon: Clock,
    title: 'Delivery Timeline',
    description: 'Standard orders ship within 1-2 business days. Most members receive their Secret within 3-5 business days.',
  },
  {
    icon: Shield,
    title: 'Adult Signature Required',
    description: 'All deliveries require an adult signature (21+). This ensures your order reaches the right hands — and only those hands.',
  },
  {
    icon: RotateCcw,
    title: 'Returns & Issues',
    description: 'Due to the nature of our products, we cannot accept returns. However, if there is a quality issue, our concierge team will make it right.',
  },
];

const SHIPPING_TIERS = [
  { method: 'Standard', time: '3-5 business days', price: '$9.99' },
  { method: 'Priority', time: '2-3 business days', price: '$14.99' },
  { method: 'Express', time: '1-2 business days', price: '$24.99' },
  { method: 'Gold & Black Members', time: '3-5 business days', price: 'Free' },
];

const ShippingPage = () => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4"
          >
            Policies
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-zinc-50 mb-6"
            data-testid="shipping-heading"
          >
            Shipping & Returns
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-sans text-sm text-zinc-500 max-w-lg mx-auto leading-relaxed"
          >
            Delivered with the same care we put into every Secret.
          </motion.p>
        </div>
      </section>

      {/* Info Cards */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
          {SHIPPING_INFO.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="border border-white/5 p-8"
                data-testid={`shipping-info-${index}`}
              >
                <Icon className="w-5 h-5 text-zinc-500 mb-5" />
                <h3 className="font-serif text-xl text-zinc-100 mb-3">{item.title}</h3>
                <p className="font-sans text-sm text-zinc-500 leading-relaxed">{item.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Shipping Rates */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-zinc-500 mb-4">Rates</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-light text-zinc-50">Shipping Options</h2>
          </motion.div>

          <div className="space-y-0">
            {SHIPPING_TIERS.map((tier, index) => (
              <motion.div
                key={tier.method}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-center justify-between py-5 border-b border-white/5"
                data-testid={`shipping-tier-${index}`}
              >
                <div>
                  <p className="font-sans text-sm text-zinc-200">{tier.method}</p>
                  <p className="font-sans text-xs text-zinc-600">{tier.time}</p>
                </div>
                <p className={`font-sans text-sm ${tier.price === 'Free' ? 'text-zinc-300' : 'text-zinc-400'}`}>{tier.price}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="font-serif text-xl text-zinc-50">Secret 13</p>
          <p className="font-sans text-xs text-zinc-600">{BRAND.legal.copyright} {BRAND.legal.membersOnly}</p>
        </div>
      </footer>
    </div>
  );
};

export default ShippingPage;
